import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

export const RESET_IN_PROGRESS_MESSAGE = 'Resetting encryption…';
export const RESET_DONE_MESSAGE = 'Encryption reset. New messages can be sent.';

export type EncryptionResetPhase = 'confirm' | 'resetting' | 'done';

export interface EncryptionResetDialogProps {
  open: boolean;
  participantId: string;
  phase?: EncryptionResetPhase;
  error?: string | null;
  onClose: () => void;
  onConfirm: () => void;
  /** After the reset landed but this browser could not fetch and unwrap its own grant: fetch it again. */
  onRetry?: () => void;
  /** The settings gate's inline fresh sign-in form while the gate waits on it, else null. */
  signInStep?: React.ReactNode;
}

/**
 * Explicit, typed confirmation for the per-conversation irreversible recovery
 * path. After the reset it shows "Resetting encryption…" until the client has
 * re-fetched and unwrapped its own grant for the new epoch, then the done line.
 */
export function EncryptionResetDialog({
  open,
  participantId,
  phase = 'confirm',
  error,
  onClose,
  onConfirm,
  onRetry,
  signInStep,
}: EncryptionResetDialogProps) {
  const [confirmation, setConfirmation] = React.useState('');
  const busy = phase === 'resetting' && !error;
  const unlockFailed = phase === 'resetting' && Boolean(error);

  React.useEffect(() => {
    if (!open) setConfirmation('');
  }, [open, participantId]);

  const confirmed = confirmation === participantId;

  return (
    <Dialog
      open={open}
      onClose={busy || signInStep ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      className="ph-no-capture" PaperProps={{ className: 'ph-no-capture' }}
      aria-labelledby="encryption-reset-title"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (confirmed && phase === 'confirm') onConfirm();
        }}
      >
        <DialogTitle id="encryption-reset-title" sx={{ fontWeight: 600 }}>
          Reset encryption for Participant {participantId}?
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {signInStep ?? (phase === 'done' ? (
            <Alert severity="success" role="status">
              {RESET_DONE_MESSAGE}
            </Alert>
          ) : unlockFailed ? (
            <Alert severity="error">{error}</Alert>
          ) : phase === 'resetting' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }} role="status">
              <CircularProgress size={20} />
              <Typography variant="body2">{RESET_IN_PROGRESS_MESSAGE}</Typography>
            </Box>
          ) : (
            <>
              <Alert severity="error">
                This is irreversible. Messages sent before the reset remain stored, but their old
                encryption keys will no longer be available in StudySync. Those messages cannot be
                recovered after this reset.
              </Alert>
              <Typography variant="body2">
                A fresh encryption key will be created for future messages with Participant{' '}
                <strong>{participantId}</strong>. Type the participant ID exactly to confirm.
              </Typography>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Type participant ID to confirm"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoFocus
                fullWidth
                autoComplete="off"
              />
            </>
          ))}
        </DialogContent>
        {!signInStep && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {phase === 'done' ? (
            <Button onClick={onClose} variant="contained">
              Close
            </Button>
          ) : unlockFailed ? (
            <>
              <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
                Close
              </Button>
              <Button onClick={onRetry} variant="contained">
                Retry
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onClose} disabled={busy} sx={{ color: 'text.secondary' }}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="error"
                variant="contained"
                disabled={!confirmed || busy}
                sx={{ minWidth: 230 }}
              >
                Reset encryption and lose old history
              </Button>
            </>
          )}
        </DialogActions>
        )}
      </form>
    </Dialog>
  );
}
